import { createSlice, createSelector } from '@reduxjs/toolkit';
import { itineraryToTransitive } from '@opentripplanner/transitive-overlay';
import coreUtils from '@opentripplanner/core-utils';
import { getShortRouteLabelFromLeg } from '#/trip-planner/utils/routes';
import { modesContainTransit } from '#/trip-planner/utils/mode';
import { tryParseOtp2LocationString } from '#/trip-planner/utils/location';
import otpUiConfig from '#/otp-ui-config';
import {
  parseFromOtpApiDateAndTimeStrings,
  formatToOtpApiDateAndTimeStrings,
  isValidOtpDateAndTime,
} from '#/shared/utils/date-time';
import {
  DATE_SELECTOR,
  TIME_SELECTOR,
  LEAVE_NOW_OPTION,
  DEPART_AT_OPTION,
  ARRIVE_BY_OPTION,
  DEFAULT_MEDIUM_ID,
  RIDER_CATEGORY_CONFLATION,
  RIDER_CATEGORY_INFO,
  PLACE_INPUT_TYPES,
} from '#/shared/constants';
import { Mode } from '#/types/otp2types.generated';
import {
  getQueryParamsByMode,
  parseQueryString,
} from '#/trip-planner/utils/query-params';
import { tripModeConfig } from '#/trip-planner/config';
import { getDetailedItineraryCostForAllCategories } from '#/trip-planner/utils/itinerary-fare';
import { itineraryToBlockIds } from '#/trip-planner/utils/itinerary';

/** @typedef {import('@opentripplanner/types').Money} Money */
/** @typedef {import('#/state/store').RootState} RootState */

const urlQuery =
  typeof window !== 'undefined'
    ? parseQueryString(window.location.search.slice(1))
    : {};

const urlDate = urlQuery[DATE_SELECTOR];
const urlTime = urlQuery[TIME_SELECTOR];

/**
 * TODO (PUBWEB-2205): Convert this module/slice to TypeScript
 *
 * This docstring provides temporary partial typing for this slice during
 * TypeScript migration. Only the properties currently required by TS consumers
 * are typed here. Additional properties can be added as needed
 *
 * @type {{
 *   activeItinerary: import('@opentripplanner/types').Itinerary | null,
 *   focusedLegIx: import('#/trip-planner/types').FocusedLegIx,
 *   transitiveData: import('@opentripplanner/types').TransitiveData | null
 * }}
 */
const initialState = {
  payLegTripId: null,
  riderCategoryId: RIDER_CATEGORY_INFO.ADULT.id,
  plan: null,
  planError: null,
  activeItinerary: null,
  focusedLegIx: null,
  transitiveData: null,
  activeItineraryIsTransit: true,
  tripTimestamp: (urlDate && urlTime
    ? parseFromOtpApiDateAndTimeStrings(urlDate, urlTime)
    : new Date()
  ).getTime(),
  // Set to false if urlDate and urlTime are populated
  // if the user is pasting a copied link or bookmark with a trip plan.
  // Otherwise, the default is to plan for the current time
  // (i.e. when the user opens the trip planner)
  leaveNow: !(urlDate && urlTime),
  currentQuery: { ...urlQuery },
  /** URL to load the most recently active trip in the app */
  lastTripUrl: null,
  /**
   * Tracks how each trip endpoint was selected by the user.
   * Used for analytics via GraphQL request extensions.
   */
  placeTypes: {
    fromPlace: urlQuery.fromPlace ? PLACE_INPUT_TYPES.URL : null,
    toPlace: urlQuery.toPlace ? PLACE_INPUT_TYPES.URL : null,
  },
};

const { convertGraphQLResponseToLegacy } = coreUtils.itinerary;
const { companies } = otpUiConfig;

const getActiveItineraryNodes = activeItinerary => {
  const transitiveDataObject = itineraryToTransitive(activeItinerary, {
    companies,
    getRouteLabel: getShortRouteLabelFromLeg,
    disableFlexArc: false,
  });

  return {
    activeItinerary,
    activeItineraryIsTransit: modesContainTransit(activeItinerary.legs),
    transitiveData: transitiveDataObject,
  };
};

const tripPlannerSlice = createSlice({
  name: 'tripPlanner',
  initialState,
  reducers: {
    fetchTripOptionsSuccess(state, action) {
      return {
        ...state,
        plan: action.payload,
        planError: null,
        ...getActiveItineraryNodes(action.payload.itineraries[0]),
      };
    },
    fetchTripOptionsError(state, action) {
      return {
        ...state,
        activeItinerary: null,
        transitiveData: null,
        focusedLegIx: null,
        plan: null,
        planError: action.payload,
      };
    },
    clearTripOptions(state) {
      return {
        ...state,
        activeItinerary: null,
        transitiveData: null,
        focusedLegIx: null,
        plan: null,
        planError: null,
      };
    },
    setActiveItinerary(state, action) {
      return {
        ...state,
        ...getActiveItineraryNodes(action.payload),
        focusedLegIx: null,
      };
    },
    setFocusedItineraryLegIx(state, action) {
      state.focusedLegIx = action.payload;
    },
    clearFocusedItineraryLeg(state) {
      state.focusedLegIx = null;
    },
    setTripTimestamp(state, action) {
      const { payload: tripTimestamp } = action;

      // our OTP engine instance expects date and time to be in Pacific time
      const [otpTripDate = null, otpTripTime = null] = tripTimestamp
        ? formatToOtpApiDateAndTimeStrings(new Date(tripTimestamp))
        : [];

      return {
        ...state,
        tripTimestamp,
        currentQuery: {
          ...state.currentQuery,
          [DATE_SELECTOR]: otpTripDate,
          [TIME_SELECTOR]: otpTripTime,
        },
      };
    },
    setCurrentQueryParam(state, action) {
      state.currentQuery = {
        ...state.currentQuery,
        ...action.payload,
      };
    },
    clearCurrentQueryParam(state, action) {
      state.currentQuery[action.payload] = null;
    },
    setLeaveNow(state, action) {
      state.leaveNow = action.payload;
    },
    setLastTripUrl(state, action) {
      state.lastTripUrl = action.payload;
    },
    setPayLegTripId(state, action) {
      state.payLegTripId = action.payload;
    },
    clearPayLegTripId(state) {
      state.payLegTripId = null;
    },
    setRiderCategoryId(state, action) {
      state.riderCategoryId = action.payload;
    },
    setPlaceType(state, action) {
      const { locationType, placeType } = action.payload;
      state.placeTypes[locationType] = placeType;
    },
  },
});

export const {
  fetchTripOptionsSuccess,
  fetchTripOptionsError,
  clearTripOptions: clearTripOptionsUntyped,
  setActiveItinerary,
  setFocusedItineraryLegIx,
  clearFocusedItineraryLeg: clearFocusedItineraryLegUntyped,
  setTripTimestamp,
  setCurrentQueryParam,
  clearCurrentQueryParam,
  setLeaveNow,
  setLastTripUrl,
  setPayLegTripId,
  clearPayLegTripId,
  setRiderCategoryId,
  setPlaceType,
} = tripPlannerSlice.actions;
export default tripPlannerSlice.reducer;

/**
 * TODO (PUBWEB-2205): Remove this typedef, the typed re-exports below, and the
 *  action aliases on above once this slice is converted to TypeScript
 *
 * While this module remains in JavaScript, annotations that use this type
 * inform TypeScript consumers that those action do not accept a payload
 *
 * @typedef {import('@reduxjs/toolkit').ActionCreatorWithoutPayload} NoPayloadAction
 */
/** @type {NoPayloadAction} */
export const clearFocusedItineraryLeg = clearFocusedItineraryLegUntyped;
/** @type {NoPayloadAction} */
export const clearTripOptions = clearTripOptionsUntyped;

export const selectCurrentQuery = s => s.tripPlanner.currentQuery;
export const selectPlaceTypes = s => s.tripPlanner.placeTypes;
export const selectActiveItineraryProperty = s => s.tripPlanner.activeItinerary;

export const selectActiveItinerary = createSelector(
  [selectActiveItineraryProperty],
  activeItinerary =>
    activeItinerary
      ? {
          ...activeItinerary,
          legs: activeItinerary.legs.map(convertGraphQLResponseToLegacy),
        }
      : null
);

/**
 * Selects only the modes property from currentQuery
 */
export const selectCurrentModes = createSelector(
  [selectCurrentQuery],
  ({ modes }) => modes
);

/**
 * Selector to transform `fromPlace` into a `Location` object.
 * Returns `undefined` when `fromPlace` is absent or malformed so that a bad
 * shared/bookmarked link doesn't crash components that read the origin.
 */
export const selectOrigin = createSelector(
  [selectCurrentQuery],
  ({ fromPlace }) =>
    fromPlace ? tryParseOtp2LocationString(fromPlace) : undefined
);

/**
 * Selector to transform `toPlace` into a `Location` object.
 * Returns `undefined` when `toPlace` is absent or malformed so that a bad
 * shared/bookmarked link doesn't crash components that read the destination.
 */
export const selectDestination = createSelector(
  [selectCurrentQuery],
  ({ toPlace }) => (toPlace ? tryParseOtp2LocationString(toPlace) : undefined)
);

/**
 * True when `fromPlace` or `toPlace` is present in the query but can't be
 * parsed into valid coordinates (e.g. a malformed URL). Used to surface a
 * coordinate-specific trip plan error instead of crashing or silently
 * planning with missing endpoints.
 */
export const selectHasInvalidEndpoints = createSelector(
  [selectCurrentQuery],
  ({ fromPlace, toPlace }) =>
    Boolean(
      (fromPlace && !tryParseOtp2LocationString(fromPlace)) ||
        (toPlace && !tryParseOtp2LocationString(toPlace))
    )
);

export const selectPrimaryMode = createSelector([selectCurrentModes], modes => {
  const availableModes = tripModeConfig.map(config => config.value);

  if (Array.isArray(modes)) {
    const selectedMode = modes.find(
      transportMode =>
        transportMode?.mode !== undefined &&
        availableModes.includes(transportMode.mode)
    );

    return selectedMode?.mode ?? Mode.Transit;
  }

  return Mode.Transit;
});

export const selectModeSpecificQueryParams = createSelector(
  [selectCurrentQuery, selectPrimaryMode],
  (currentQuery, primaryMode) => getQueryParamsByMode(primaryMode, currentQuery)
);

/**
 * Selects only the properties needed for determining depart/arrive mode
 */
export const selectDateTimeProperties = createSelector(
  [selectCurrentQuery],
  ({ date, time, arriveBy }) => ({ date, time, arriveBy })
);

/**
 * Selects the leaveNow property directly from tripPlanner state
 */
export const selectLeaveNow = createSelector(
  [state => state.tripPlanner],
  ({ leaveNow }) => leaveNow
);

/**
 * Determines departure/arrival mode based on query parameters
 * @type {import('@reduxjs/toolkit').Selector<import('#/state/store').RootState, "LEAVE_NOW" | "DEPART" | "ARRIVE">}
 */
export const selectDepartArrive = createSelector(
  [selectDateTimeProperties, selectLeaveNow],
  ({ date, time, arriveBy }, leaveNow) => {
    // Priority 1: Arrive by mode
    if (arriveBy) return ARRIVE_BY_OPTION;

    // Priority 2: Leave now mode
    if (leaveNow) return LEAVE_NOW_OPTION;

    // Priority 3: Check if we have valid date and time using our utility function
    if (isValidOtpDateAndTime(date, time)) {
      return DEPART_AT_OPTION; // Valid date and time, and not leaveNow mode
    }

    // Default to NOW if any validation fails
    return LEAVE_NOW_OPTION;
  }
);

export const selectActiveItineraryLegs = createSelector(
  [selectActiveItinerary],
  activeItinerary => activeItinerary?.legs || []
);

export const selectPayLegTripId = createSelector(
  [state => state.tripPlanner.payLegTripId], // guard against missing slice
  id => id
);

export const selectLegByTripId = createSelector(
  [selectActiveItineraryLegs, selectPayLegTripId],
  (legs, payLegTripId) => {
    if (!legs.length || !payLegTripId) {
      return null;
    }

    return legs.find(leg => leg.trip?.id === payLegTripId) || null;
  }
);

/**
 * Selects fare breakdown information for the conflation rider categories
 * (Adult, Honored Citizen, Youth) keyed by the conflation entry id.
 *
 * Each breakdown's total is computed by summing per-leg fares where each leg
 * tries the conflation entry's mapped rider category ids in priority order
 * (e.g. Honored Citizen tries [HONORED_CITIZEN, DISCOUNTED]) before falling
 * back to the leg's default rider category to prevent a leg from displaying
 * as free.
 *
 * @returns {Object|null} Fare breakdowns object keyed by conflation id, or
 *   null if no active itinerary
 */
export const selectFareBreakdowns = createSelector(
  [state => state.tripPlanner.activeItinerary],
  activeItinerary => {
    if (!activeItinerary?.legs) {
      return null;
    }
    return (
      getDetailedItineraryCostForAllCategories(
        activeItinerary.legs,
        DEFAULT_MEDIUM_ID,
        RIDER_CATEGORY_CONFLATION
      ) || null
    );
  }
);

/**
 * Selects formatted rider category options for the fare selector dropdown.
 * Rows are emitted in the order defined by `RIDER_CATEGORY_CONFLATION` so the
 * dropdown is deterministic. An entry is omitted when no leg in the itinerary
 * produced a cost for it (e.g. itineraries with no transit legs).
 *
 * @returns {Array<{value: string, totalCost: Money}>} Array of rider category options
 */
export const selectRiderCategoryOptions = createSelector(
  [selectFareBreakdowns],
  fareBreakdowns => {
    if (!fareBreakdowns) {
      return [];
    }

    return RIDER_CATEGORY_CONFLATION.filter(
      entry => fareBreakdowns[entry.id]
    ).map(entry => ({
      value: entry.id,
      i18nKey: entry.i18nKey,
      totalCost: fareBreakdowns[entry.id].totalCost,
    }));
  }
);

export const selectRiderCategoryId = createSelector(
  [state => state.tripPlanner.riderCategoryId],
  riderCategoryId => riderCategoryId || null
);

/**
 * Selects leg cost information for the current rider category and a specific
 * trip ID
 *
 * @type {(
 *   state: RootState, // Redux state
 *   tripId?: string // The trip ID to look up
 * ) => ({
 *   price: Money, // The fare price for this leg
 *   isDependent: boolean // Whether fare is dependent on another fare product
 * } | null)}
 */
export const selectLegCostByTripId = createSelector(
  [selectFareBreakdowns, selectRiderCategoryId, (state, tripId) => tripId],
  (fareBreakdowns, riderCategoryId, tripId) => {
    if (!fareBreakdowns || !riderCategoryId || !tripId) {
      return null;
    }

    return fareBreakdowns[riderCategoryId]?.legCosts?.[tripId] || null;
  }
);

export const selectActiveItineraryBlockIds = createSelector(
  [selectActiveItinerary],
  activeItinerary =>
    activeItinerary ? itineraryToBlockIds(activeItinerary) : []
);
